import { RdpKeyboardLayout } from "@cloudserver/contracts";
import { useSyncExternalStore } from "react";
import type { SettingsStorage } from "./settings.js";

/*
 * «Настройки сеанса» of the screens — the server console (VNC) and remote
 * desktops: scale, and the keyboard layout the keys are typed in. Per person
 * and browser in localStorage, like the terminal's settings (`settings.ts`).
 */

/** `fit`: the whole screen scaled into the window; `actual`: pixel for pixel, scrolled. */
export type ScaleMode = "fit" | "actual";
/**
 * The layout the console's server is switched to: the console sends keys,
 * not text, so a character is typed as the key that makes it in that layout.
 */
export type ConsoleLayout = "us" | "ru";

export interface ScreenSettings {
  scale: ScaleMode;
  consoleLayout: ConsoleLayout;
  /** guacd's layout for remote desktops; `default` sends none (guacd's US layout). */
  rdpLayout: RdpKeyboardLayout | "default";
}

export const DEFAULT_SCREEN_SETTINGS: ScreenSettings = {
  scale: "fit",
  consoleLayout: "us",
  rdpLayout: "default",
};

const KEY = "cs.sessions.screen";

/** Settings from stored text; anything unknown or damaged falls back to the default. */
export function parseScreenSettings(text: string | null): ScreenSettings {
  let raw: Partial<Record<keyof ScreenSettings, unknown>> = {};
  try {
    const parsed: unknown = text ? JSON.parse(text) : {};
    if (parsed && typeof parsed === "object") raw = parsed as typeof raw;
  } catch {
    // Damaged JSON: the defaults.
  }
  return {
    scale: raw.scale === "actual" ? "actual" : "fit",
    consoleLayout: raw.consoleLayout === "ru" ? "ru" : "us",
    rdpLayout: RdpKeyboardLayout.safeParse(raw.rdpLayout).data ?? "default",
  };
}

const browserStorage = (): SettingsStorage | null => {
  try {
    return localStorage;
  } catch {
    return null;
  }
};

export class ScreenSettingsStore {
  private state: ScreenSettings;
  private readonly listeners = new Set<() => void>();

  constructor(private readonly storage: SettingsStorage | null = browserStorage()) {
    let text: string | null = null;
    try {
      text = storage?.getItem(KEY) ?? null;
    } catch {
      // Storage refused: the defaults.
    }
    this.state = parseScreenSettings(text);
  }

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly snapshot = (): ScreenSettings => this.state;

  update(patch: Partial<ScreenSettings>): void {
    const next = parseScreenSettings(JSON.stringify({ ...this.state, ...patch }));
    this.state = next;
    try {
      this.storage?.setItem(KEY, JSON.stringify(next));
    } catch {
      // Storage refused: the change holds until the tab closes.
    }
    for (const listener of this.listeners) listener();
  }
}

export const screenSettings = new ScreenSettingsStore();

export function useScreenSettings(): ScreenSettings {
  return useSyncExternalStore(
    screenSettings.subscribe,
    screenSettings.snapshot,
    screenSettings.snapshot,
  );
}
